import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useBeforeunload } from 'react-beforeunload';
import FullCalendar from '@fullcalendar/react';
import dayjs from 'dayjs';
import clsx from 'clsx';
import { DateSpanApi, DatesSetArg, EventContentArg, EventDropArg } from '@fullcalendar/core';
import interactionPlugin, { DateClickArg, EventDragStartArg } from '@fullcalendar/interaction';
import scrollGridPlugin from '@fullcalendar/scrollgrid';
import 'dayjs/locale/cs';

import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid';
import { ResourceLabelContentArg } from '@fullcalendar/resource/index.js';
import { EventImpl } from '@fullcalendar/core/internal';
import { useTranslation } from 'react-i18next';
import csLocale from '@fullcalendar/core/locales/cs';
import enLocale from '@fullcalendar/core/locales/en-gb';
import deLocale from '@fullcalendar/core/locales/de';
import huLocale from '@fullcalendar/core/locales/hu';
import plLocale from '@fullcalendar/core/locales/pl';
import frLocale from '@fullcalendar/core/locales/fr';

import { PermissionTypesEnum, ReservationsDataOutDTO } from '@api/ringil-api.v2';
import { ApiContext } from '@api/api.tsx';
import { useLocalStorage } from '@utils/utils';
import { formatSimpleDateTimeFromDayJs } from '@utils/dateWithTz';
import useTranslationLgs from '@hooks/i18n/useTranslation';
import useNavigation from '@hooks/useNavigation';
import { AuthenticatedUserContext } from '@components/auth/AuthenticatedUser.tsx';
import { useCreateTimeslotSidebar } from '@components/ringil3/Features/Reservation/Sidedbars/CreateTimeslotSidebar.tsx';
import { CalendarEventCard } from '@components/ringil3/CalendarEventCard/CalendarEventCard';
import { useCalendarLayoutConfig } from '@components/features/calendar-layout/context';
import useBetaVersion from '@hooks/useBetaVersion/useBetaVersion';
import { BetaSurface } from '@hooks/useBetaVersion/betaSurfaces';
import { BUILT_IN_CARD_LAYOUT, BUILT_IN_DETAIL_LAYOUT, layoutFromLegacyFragments } from '@components/features/calendar-layout/defaults';
import {
  deriveSelectedParents,
  FilterConfig,
  FilterType,
  FilterUiVersionSwitch,
  RingilFilterBar,
  useFilterUiVersion,
} from '@components/RingilFilters';
import SvgChevron from '@icons/chevron.svg?react';
import RingilButton from '@components/ringil3/Buttons/RingilButton.tsx';
import { YardTable } from '@app/pages/timeslots/YardTable.tsx';
import { useCreateSettingsSidebar } from '../sidebars/SettingsSidebar';
import { CALENDAR_FILTER_FIELDS, CALENDAR_FILTER_NAME, EMPTY_CALENDAR_FILTER, isOwnLocation, useCalendarFilter } from './useCalendarFilter';
import { hasStaleSelection, pruneCalendarSelection } from './calendarSelection';
import { buildHighlightOptions, highlightStateOf, isHighlightActive } from './calendarHighlight';
import { CalendarViewType } from './calendarView';
import { sortResourcesByTimezoneOffset } from './calendarEventMapping';
import { useCalendarData } from './useCalendarData';
import { useCalendarLayout } from './useCalendarLayout';
import { useCalendarStats } from './useCalendarStats';
import { RampHeader } from './components/RampHeader';
import { CalendarToolbar } from './components/CalendarToolbar';
import { CalendarSummary } from './components/CalendarSummary';
import { CalendarStats } from './components/CalendarStats';
import { CalendarSettings } from './components/CalendarSettings';
import { LegacyLocationRampFilter } from './components/LegacyLocationRampFilter';

// The historical default fragment list now lives as BUILT_IN_CARD_LAYOUT in the calendar-layout
// feature, which reproduces it zone for zone.

export const TimeslotCalendar: React.FC = () => {
  const navigation = useNavigation();
  const { tr } = useTranslationLgs();
  const { i18n } = useTranslation();
  const { loggedUser, hasPermission } = useContext(AuthenticatedUserContext);
  const {
    updateReservationTimeslot,
    listCustomDataByType,
    getUserTimeslotCalendarFilters,
    saveUserTimeslotCalendarFilters,
    deleteUserFilter,
  } = useContext(ApiContext);

  const [currentDate, setCurrentDate] = useLocalStorage<string>('TIMESLOT_SELECTED_DATE', formatSimpleDateTimeFromDayJs(dayjs()));
  const [currentView, setCurrentView] = useLocalStorage<CalendarViewType>('CALENDAR_VIEW', 'resourceTimeGridDay');
  const [detailRampViewId, setDetailRampViewId] = useState<string | null>(null);
  // Null until the company custom-data call answers, so "no configuration" stays distinguishable
  // from "configured to the historical default".
  const [legacyCardFragments, setLegacyCardFragments] = useState<string[] | null>(null);
  const { layoutFor, resolutionFor, loaded: layoutLoaded, reload: reloadLayouts } = useCalendarLayoutConfig();
  // Which card the calendar draws: the configured layout model, or the fixed one it always had.
  // Set per company in the Beta features admin — there is no end-user switch for this surface.
  const { showLegacy: legacyCards } = useBetaVersion(BetaSurface.TIMESLOT_CALENDAR_CARD);
  const [sidebarIntial, setSidebarInitial] = useState(false);

  const { SettingsSidebar, visible, setVisible } = useCreateSettingsSidebar();
  const { CreateTimeslotSidebar, createTimeslotRequest, setCreateTimeslotRequest } = useCreateTimeslotSidebar();

  // Refs
  const calendarRef = useRef(null);
  const fakeElemRef = useRef<HTMLElement>(null);
  const calendarWrapperRef = useRef<HTMLDivElement>(null);

  const canEdit = hasPermission([PermissionTypesEnum.PERMISSION_TIMESLOT_DASHBOARD_RW]);

  const { filter, applyFilterValue, applyFilter, ready: filterReady } = useCalendarFilter();
  const selectedLocationIds = filter?.locationIds ?? [];
  const selectedRampIds = filter?.rampIds ?? [];

  // Same global preference the tables use, so a user who switched back to the legacy filtering UI
  // gets the calendar's original location → ramp panel here.
  const { version: filterUiVersion, toggle: toggleFilterUiVersion, switchVisible: filterSwitchVisible } = useFilterUiVersion();
  const useLegacyFilter = filterUiVersion === 'legacy';

  const { ramps, rampOrderLookup, events, openingHours, locations, locationsWithRamps, locationsLoaded, reload } = useCalendarData({
    locationIds: selectedLocationIds,
    rampIds: selectedRampIds,
    currentDate,
    currentView,
    enabled: filterReady,
  });

  /**
   * A picked location can be deleted or superseded afterwards, while the selection lives on in the
   * URL, in local storage and in saved filters. The dropdown is built from the loaded location list
   * and stops offering it, so drop it from the live filter as well once that list has answered —
   * `useCalendarData` never requests an unknown id, so nothing is loaded for it in the meantime.
   */
  useEffect(() => {
    // An empty list is never taken as "everything is gone" — a failed or still-pending load must not
    // wipe a selection the user would have to rebuild by hand.
    if (!filterReady || !locationsLoaded || locations.length === 0) return;
    const selection = { locationIds: selectedLocationIds, rampIds: selectedRampIds };
    if (!hasStaleSelection(selection, locations)) return;
    applyFilter({ ...filter, ...pruneCalendarSelection(selection, locations) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterReady, locationsLoaded, locations, selectedLocationIds, selectedRampIds]);

  const getLocationName = (locationId?: string) => locationsWithRamps.find(loc => loc.locationId === locationId)?.name ?? '';

  const toResource = (t: {
    rampId: string;
    name: string;
    timezoneId?: string;
    timezoneOffset?: number;
    locationId?: string
  }) => ({
    id: t.rampId,
    title: t.name,
    // FullCalendar re-sorts the columns through `resourceOrder`, so the manual ramp order has to
    // travel with the resource rather than only in the array order.
    rampOrder: rampOrderLookup.get(t.rampId) ?? Number.MAX_SAFE_INTEGER,
    extendedProps: {
      timezoneId: t.timezoneId,
      timezoneOffset: t.timezoneOffset,
      locationId: t.locationId,
      locationName: getLocationName(t.locationId),
      rampOrder: rampOrderLookup.get(t.rampId) ?? Number.MAX_SAFE_INTEGER,
    },
  });

  const allRampsResources = ramps.map(toResource);
  const rampsResources = ramps.filter(r => selectedRampIds.includes(r.rampId)).map(toResource);

  const {
    dayMinWidth,
    calendarStep,
    visibleRampsCount,
    setVisibleRampsCount,
    showLargeView,
    setShowLargeView,
    nextRampDisabled,
    prevRampDisabled,
    lastCurrentDate,
    setLastCurrentDate,
    weekDisabled,
    resetCalendarShift,
    changeDateCal,
    handleMoveClick,
    restrictCalendarCols,
    handleViewToggle,
    changeWeekViewRamp,
  } = useCalendarLayout({
    calendarRef,
    rampsResources,
    currentDate,
    setCurrentDate,
    currentView,
    setCurrentView,
    detailRampViewId,
    setDetailRampViewId,
  });

  const {
    statisticsEnabled,
    stats,
    statsOpened,
    setStatsOpened,
    statsActive,
    setStatsActive,
  } = useCalendarStats({ currentDate, currentView, ramps });

  // Filter

  /**
   * Two trees over the same request keys: the company's own locations and everything foreign
   * (external / partner / shared with us / inherited from a customer). Each chip edits only the ids
   * of its own locations, so `locationIds`/`rampIds` stay a single merged pair for the API.
   */
  const filtersConf: FilterConfig[] = useMemo(() => {
    const toTreeOptions = (locations: typeof locationsWithRamps) =>
      locations.map(l => ({
        value: l.locationId,
        label: l.name,
        children: l.ramps.map(r => ({ value: r.rampId, label: r.name, active: true })),
      }));

    const ownLocations = locationsWithRamps.filter(isOwnLocation);
    const foreignLocations = locationsWithRamps.filter(l => !isOwnLocation(l));

    const confs: FilterConfig[] = [];

    // Either tree is dropped entirely when it has nothing to pick from — an empty chip is useless.
    if (ownLocations.length > 0) {
      confs.push({
        filterType: FilterType.TreeMultiSelect,
        id: 'ownLocations',
        label: tr('TimeslotReservations.OurLocations', 'Our locations'),
        placeholder: tr('TimeslotReservations.SearchLocationOrRamp', 'Search location or ramp'),
        value: CALENDAR_FILTER_FIELDS,
        treeOptions: toTreeOptions(ownLocations),
        defaultShown: true,
      });
    }

    if (foreignLocations.length > 0) {
      confs.push({
        filterType: FilterType.TreeMultiSelect,
        id: 'foreignLocations',
        label: tr('TimeslotReservations.ForeignLocations', 'Foreign locations'),
        placeholder: tr('TimeslotReservations.SearchLocationOrRamp', 'Search location or ramp'),
        value: CALENDAR_FILTER_FIELDS,
        treeOptions: toTreeOptions(foreignLocations),
        defaultShown: true,
      });
    }

    // Highlight-only chips. Their options are the values actually present in the loaded reservations,
    // so a pick can never grey out everything, and they never touch the request (calendarHighlight.ts).
    const highlightOptions = buildHighlightOptions(events);
    confs.push(
      {
        filterType: FilterType.MultiSelect,
        label: tr('TimeslotReservations.ReservationId', 'Reservation ID'),
        value: ['applicationIds'],
        options: highlightOptions.applicationIds,
      },
      {
        filterType: FilterType.MultiSelect,
        label: tr('TimeslotReservations.CarPlate', 'Car plate'),
        value: ['carPlates'],
        options: highlightOptions.carPlates,
      },
      {
        filterType: FilterType.MultiSelect,
        label: tr('TimeslotReservations.CarrierOrPartner', 'Carrier / Partner'),
        value: ['carrierIds'],
        options: highlightOptions.carriers,
      },
      {
        filterType: FilterType.MultiSelect,
        label: tr('TimeslotReservations.ConnectedExpeditionId', 'Connected expedition ID'),
        value: ['expeditionApplicationIds'],
        options: highlightOptions.expeditionApplicationIds,
      },
      {
        filterType: FilterType.MultiSelect,
        label: tr('TimeslotReservations.ConnectedSupplier', 'Connected supplier'),
        value: ['supplierIds'],
        options: highlightOptions.suppliers,
      },
    );

    return confs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationsWithRamps, events]);

  /** Ramp selection is the source of truth; the location ids follow from it (all ramps ticked). */
  const applyRampSelection = (rampIds: string[]) => {
    const allGroups = filtersConf.flatMap(c => c.treeOptions ?? []);
    applyFilter({ ...filter, rampIds, locationIds: deriveSelectedParents(allGroups, rampIds) });
  };

  // Utils

  const saveScrollPosition = () => {
    if (calendarRef?.current?.elRef?.current) {
      const value = calendarRef.current.elRef.current.scrollTop.toString() || '0';
      sessionStorage.setItem('scrollY', value.toString());
    }
  };

  const scrollToCurrentTime = () => {
    // Custom implementation, because FC scrollTime and scrollToTime not working
    const calendar = calendarRef?.current as any;
    if (calendar) {
      const cellHeight = showLargeView ? 150 : 54;
      const hoursThreshold = 1;
      const currentHour = dayjs().hour() - hoursThreshold;
      const slotsPerHour = 2;
      const scrollToValue = cellHeight * currentHour * slotsPerHour;

      if (scrollToValue > 0) {
        calendar?.elRef?.current?.scrollTo(0, scrollToValue);
      }
    }
  };

  const findLocationIdByRampId = (id?: string) => {
    if (!id) {
      return null;
    }
    return locationsWithRamps.find(loc => loc.ramps.find(r => r.rampId === id))?.locationId;
  };

  const changeDate = (date: DatesSetArg) => {
    setCurrentDate(date.startStr);

    if (dayjs().isSame(date.startStr, 'day')) {
      scrollToCurrentTime();
    }
  };

  const getResources = useCallback(() => {
    const source = rampsResources.length > 0 ? rampsResources : allRampsResources;
    return detailRampViewId ? source.filter(rr => rr.id === detailRampViewId) : source;
  }, [rampsResources, detailRampViewId, allRampsResources]);

  const getEvents = useCallback(() => {
    if (detailRampViewId) {
      const filteredEvents = events.filter(e => e.resourceId === detailRampViewId);
      if (openingHours) {
        return [...filteredEvents, ...openingHours.filter(e => e.resourceId === detailRampViewId)];
      }
      return [...filteredEvents];
    }
    return openingHours ? [...events, ...openingHours] : [...events];
  }, [detailRampViewId, events, openingHours]);

  // Render helpers

  const renderHeader = (data: ResourceLabelContentArg) => {
    const currentId = data?.resource?._resource?.id;
    const relevantEventsCount = currentId ? events.filter(evt => evt?.rampId === currentId)?.length : 0;

    return (
      <RampHeader
        rampId={currentId}
        title={data.resource.title}
        locationName={data.resource.extendedProps?.locationName}
        timezoneId={data.resource.extendedProps?.timezoneId}
        reservationsCount={relevantEventsCount}
        reservationsLabel={tr('TimeslotReservations.ReservationsAmount', 'Reservations Amount')}
      />
    );
  };

  /**
   * Resolution order per event: the configured layout for that event's location (the backend
   * resolves location → company → system default for us); otherwise the legacy per-company fragment
   * list; otherwise the built-in layout the calendar has always drawn.
   *
   * Resolved per event rather than once for the view because one calendar shows ramps from several
   * locations at a time, and each location may run a different layout.
   */
  const legacyLayout = useMemo(
    () => (legacyCardFragments?.length ? layoutFromLegacyFragments(legacyCardFragments) : null),
    [legacyCardFragments],
  );
  const layoutsFor = (locationId?: string) => {
    // On the original version the configured models are ignored entirely — not merely unresolved —
    // so the switch is a true before/after of the same calendar.
    if (!legacyCards && layoutLoaded && resolutionFor(locationId)) {
      const config = layoutFor(locationId);
      return { card: config.card, detail: config.detail };
    }
    // The legacy list only ever described the card, so the popover keeps its built-in layout.
    return { card: legacyLayout ?? BUILT_IN_CARD_LAYOUT, detail: BUILT_IN_DETAIL_LAYOUT };
  };

  const renderEventContent = (data: EventContentArg) => {
    if (data.event.display === 'background') {
      return <div className="relative z-0 w-full h-full" style={{ backgroundColor: data.event.backgroundColor }}></div>;
    }
    const reservation = data.event.extendedProps as ReservationsDataOutDTO;
    if (reservation.anonymous) {
      // Carrier-side calendar: other carriers' reservations shown as anonymous boxes (no detail, no link),
      // using the same background as a normal reservation card.
      // cursor-default overrides FullCalendar's inherited .fc-event pointer cursor — the box is not
      // clickable (no detail, no redirect), so it must not show the "hand".
      // Anonymous boxes carry no data to match on, so an active highlight filter always dims them.
      return (
        <div
          className={clsx('relative z-0 w-full h-full cursor-default', isHighlightActive(filter) && 'opacity-40 grayscale')}>
          <div
            className="custom-fullcalendar-event bg-base-base border border-gray-200 border-solid w-full h-full absolute left-0 z-0 top-0 rounded-lg cursor-default" />
        </div>
      );
    }
    const link = navigation.createNavigationLink(navigation.urlFunctions.createReservationDetail(reservation.reservationId));
    const { card: cardLayout, detail: detailLayout } = layoutsFor(reservation.locationId);
    return (
      <CalendarEventCard
        data={reservation}
        link={link}
        layout={cardLayout}
        detailLayout={detailLayout}
        onStateChange={reload}
        highlight={highlightStateOf(reservation, filter)}
      />
    );
  };

  // Event handlers

  const handleEventDrop = (info: EventDropArg) => {
    const date = info.event.start;
    const sinceHourString = dayjs(info.event.start).format('HH:mm');
    const reservationId = info.event?._def?.extendedProps?.reservationId;
    const rampId = info.event?._def?.extendedProps?.rampId;
    const timeslot = info.event?._def?.extendedProps?.timeslot;
    const newRamp = info?.newResource?._resource.id ?? rampId;
    const localDate = dayjs(date).format('YYYY-MM-DD');
    const durationInMinutes = dayjs(timeslot.till).diff(dayjs(timeslot.since), 'minutes');

    updateReservationTimeslot(
      reservationId,
      {
        durationInMinutes,
        localDate,
        rampId: newRamp,
        sinceHourString,
      },
      () => false,
    );
  };

  const handleDateClick = (info: DateClickArg) => {
    if (canEdit) {
      const rampId = info?.resource?._resource?.id || detailRampViewId;
      const location = locationsWithRamps?.find(loc => loc.ramps.find(r => r.rampId?.includes(rampId)));
      const locationId = location?.locationId;
      const ramp = location?.ramps.find(r => r.rampId === rampId);

      setCreateTimeslotRequest({
        applicationId: null,
        companyLocationId: locationId ?? null,
        preferredDate: info?.dateStr ? info.dateStr.substring(0, 10) : currentDate,
        companyLocationCustomerId: location?.companyId ?? loggedUser.companyId,
        expeditionLocationId: null,
        expeditionId: null,
        rampId: rampId ?? null,
        arrivalHourString: info?.dateStr ? dayjs(info?.dateStr).format('HH:mm') : null,
        timezoneId: ramp?.timezoneId ?? null,
      });
    }
  };

  const handleEventDragStart = (arg: EventDragStartArg) => {
    //@ts-ignore
    const locationId = arg?.event?._def?.extendedProps?.locationId;
    const rampsFromLocation = locationsWithRamps.filter(loc => loc.locationId === locationId).flatMap(l => l.ramps);

    // Set background for disabled ramps (other locations)
    allRampsResources
      .filter(r => !rampsFromLocation.some(ar => ar.rampId === r.id))
      .forEach(x => document.querySelector(`.fc-day.fc-timegrid-col[data-resource-id="${x.id}"]`)?.classList.add('custom-fullcalendar-column'));

    // Keep original event block on its place during drag
    const originalElem = arg.el;
    const fakeElem = originalElem.cloneNode(true) as HTMLElement;
    const rect = originalElem.getBoundingClientRect();

    fakeElem.classList.add('fake-event-element');
    fakeElem.style.top = `${rect.top + window.scrollY}px`;
    fakeElem.style.left = `${rect.left + window.scrollX}px`;
    fakeElem.style.width = `${rect.width}px`;
    fakeElem.style.height = `${rect.height}px`;
    document.body.appendChild(fakeElem);
    fakeElemRef.current = fakeElem;
  };

  const handleEventDragStop = () => {
    // Delete original block after drag
    if (fakeElemRef.current) {
      fakeElemRef.current.classList.remove('fake-event-element');
      fakeElemRef.current.remove();
      fakeElemRef.current = null;
    }
    const column = document.querySelectorAll('.fc-day.fc-timegrid-col');
    column.forEach(col => col.classList.remove('custom-fullcalendar-column'));

    // Refresh events data after drag (to update time) — the write above needs a moment to land.
    setTimeout(() => reload(), 500);
  };

  const handleEventAllow = (dropInfo: DateSpanApi, draggedEvent: EventImpl) => {
    // Allow drop only for ramps from location
    const fromLocationId = findLocationIdByRampId(dropInfo?.resource._resource.id);
    const targetLocationId = findLocationIdByRampId(draggedEvent?._def.extendedProps.rampId);
    return fromLocationId === targetLocationId;
  };

  const handleLocale = (() => {
    switch (i18n.language) {
      case 'cs':
        return csLocale;
      case 'de':
        return deLocale;
      case 'hu':
        return huLocale;
      case 'pl':
        return plLocale;
      case 'fr':
        return frLocale;
      default:
        return enLocale;
    }
  })();

  // Hooks
  useBeforeunload(() => {
    saveScrollPosition();
  });

  useLayoutEffect(() => {
    // The configured layouts, refetched on every open of the calendar: a layout published while the
    // app was already running must show up on the next visit, not only after a page reload.
    // Requested here rather than app-wide because only the calendar and the designer need them.
    reloadLayouts();
    // Legacy per-company fragment list, still read as the fallback for companies the layout
    // migration has not covered.
    listCustomDataByType('CALENDAR_CARD_FRAGMENTS', d => {
      if (d.length > 0) {
        const data = d[0].payload;
        setLegacyCardFragments(data.split(',').map((t: string) => t.trim()));
      }
    });
  }, []);

  useEffect(() => {
    if (detailRampViewId) {
      changeWeekViewRamp(true);
      // Workaround for Safari repaint problem. "Standard" rerenderXxx() or refetchXxx() didn't work
      const api = calendarRef?.current?.getApi();
      if (api) {
        const evts = getEvents();
        api.removeAllEvents();
        evts.forEach(event => api.addEvent(event));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detailRampViewId]);

  // Changing the ramp selection changes the set of columns, so the horizontal paging offset
  // (previously reset from every checkbox handler) has to go back to the first page.
  useEffect(() => {
    resetCalendarShift();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRampIds.join(',')]);

  useEffect(() => {
    if (visibleRampsCount && rampsResources.length > 0 && !sidebarIntial && currentView) {
      restrictCalendarCols(visibleRampsCount);
      setVisibleRampsCount(visibleRampsCount);
      handleViewToggle(currentView === 'resourceTimeGridDay' ? 'day' : currentView === 'timeGridWeek' ? 'week' : 'workWeek');
      setSidebarInitial(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleRampsCount, rampsResources]);

  useEffect(() => {
    const el = calendarRef?.current?.elRef?.current;
    if (el) {
      const scrollY = parseInt(sessionStorage.getItem('scrollY') || '0', 10);
      el.scrollTo(0, scrollY);
    }
  }, [calendarRef?.current?.elRef?.current]);

  useEffect(() => {
    setLastCurrentDate(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <CalendarToolbar
        currentDate={currentDate}
        onDateChange={changeDateCal}
        onNewReservation={() =>
          setCreateTimeslotRequest({
            applicationId: null,
            companyLocationId: null,
            preferredDate: currentDate.substring(0, 10),
            companyLocationCustomerId: null,
            expeditionLocationId: null,
            expeditionId: null,
            timezoneId: null,
          })
        }
        onCustomise={() => setVisible(true)}
      />

      <YardTable locations={locations} />

      <div className="flex flex-row items-center gap-x-2 w-full mb-4">
        {useLegacyFilter ? (
          <LegacyLocationRampFilter locationsWithRamps={locationsWithRamps} rampIds={selectedRampIds}
                                    onChange={applyRampSelection} />
        ) : (
          <div className="grow">
            <RingilFilterBar
              filtersConf={filtersConf}
              filterName={CALENDAR_FILTER_NAME}
              filter={filter}
              applyFilterValue={applyFilterValue}
              applyFilter={applyFilter}
              // Reset restores the full empty shape rather than {}, so a filter saved right after a
              // reset still round-trips through the API's CalendarFilter contract.
              normalizeFilter={f => ({ ...EMPTY_CALENDAR_FILTER, ...f })}
              getSavedFilters={getUserTimeslotCalendarFilters}
              saveFilters={saveUserTimeslotCalendarFilters}
              deleteSavedFilter={deleteUserFilter}
              filterUiVersion="v2"
            />
          </div>
        )}
      </div>

      <CalendarSummary events={events} />

      {statisticsEnabled && stats && statsOpened && statsActive.includes(true) && (
        <CalendarStats stats={stats} statsActive={statsActive} currentView={currentView} />
      )}

      {(currentView === 'timeGridWeek' || currentView === 'timeGridWorkWeek') && (
        <div className="mb-4 flex flex-row gap-x-6 items-center justify-between">
          <div className="text-zinc-600 text-lg">{rampsResources.find(r => r.id === detailRampViewId)?.title}</div>

          <div className="flex flex-row gap-x-2 items-center">
            <RingilButton
              mode="table"
              onClick={() => changeWeekViewRamp(false, 'prev')}
              icon={<SvgChevron className="h-4 w-4 rotate-90" />}
              disabled={prevRampDisabled}
              tooltip={tr('TimeslotReservations.PreviousRamp', 'Previous ramp')}
              className="h-8 border border-content-tertiary px-3 py-0"
              dataTestId="calendar-prev-ramp"
            />

            <span
              className="text-content-secondary text-sm font-medium">{`${rampsResources.findIndex(r => r.id === detailRampViewId) + 1} / ${
              rampsResources.length
            } ramp`}</span>

            <RingilButton
              mode="table"
              onClick={() => changeWeekViewRamp(false, 'next')}
              icon={<SvgChevron className="h-4 w-4 -rotate-90" />}
              disabled={nextRampDisabled}
              tooltip={tr('TimeslotReservations.NextRamp', 'Next ramp')}
              className="h-8 border border-content-tertiary px-3 py-0"
              dataTestId="calendar-next-ramp"
            />
          </div>
        </div>
      )}

      {filterReady && (
        <div className="relative overflow-x-auto">
          {visibleRampsCount && !detailRampViewId && (calendarStep + visibleRampsCount - 1 < rampsResources.length || calendarStep > 1) && (
            <div
              // pl clears the time gutter on the left; pr keeps the right arrow just inside the last
              // ramp column rather than over its content.
              className="z-10 absolute w-full pointer-events-none flex justify-between top-[2px] pl-[2.0625rem] pr-1">
              <RingilButton
                mode="table"
                onClick={() => handleMoveClick('left')}
                icon={<SvgChevron className="h-4 w-4 rotate-90" />}
                disabled={calendarStep <= 1}
                tooltip={tr('TimeslotReservations.ScrollRampsLeft', 'Show previous ramps')}
                className="pointer-events-auto h-8 border border-content-tertiary px-3 py-0"
                dataTestId="calendar-ramps-left"
              />
              <RingilButton
                mode="table"
                onClick={() => handleMoveClick('right')}
                icon={<SvgChevron className="h-4 w-4 -rotate-90" />}
                disabled={calendarStep + visibleRampsCount - 1 >= rampsResources.length}
                tooltip={tr('TimeslotReservations.ScrollRampsRight', 'Show next ramps')}
                className="pointer-events-auto h-8 border border-content-tertiary px-3 py-0"
                dataTestId="calendar-ramps-right"
              />
            </div>
          )}

          <div ref={calendarWrapperRef} className="h-full">
            <div id="calendar"
                 className={clsx('relative calendar-multi h-full', showLargeView ? 'calendar-extended' : 'calendar-condensed')}>
              <FullCalendar
                views={{
                  timeGridWeek: {
                    type: 'timeGrid',
                    duration: { days: 7 },
                  },
                  timeGridWorkWeek: {
                    type: 'timeGrid',
                    duration: { days: 5 },
                  },
                }}
                ref={calendarRef}
                dayMinWidth={dayMinWidth ?? undefined}
                height="auto"
                headerToolbar={false}
                plugins={[resourceTimeGridPlugin, interactionPlugin, scrollGridPlugin]}
                schedulerLicenseKey="0801397464-fcs-1643209021"
                eventContent={renderEventContent}
                eventOverlap={false}
                eventAllow={handleEventAllow}
                eventDragStart={handleEventDragStart}
                eventDragStop={handleEventDragStop}
                initialView="resourceTimeGridDay"
                resourceLabelContent={renderHeader}
                locale={handleLocale}
                contentHeight={800}
                nowIndicator
                initialDate={currentDate}
                datesSet={changeDate}
                resourceOrder={sortResourcesByTimezoneOffset}
                allDaySlot={false}
                resources={getResources()}
                events={openingHours && events && getEvents()}
                eventDisplay="block"
                editable={canEdit}
                droppable={canEdit}
                eventDrop={handleEventDrop}
                dateClick={handleDateClick}
              />
            </div>
          </div>

          {createTimeslotRequest && <CreateTimeslotSidebar onContinue={() => reload()} headerDisabled={false} />}
        </div>
      )}

      {/* Below the calendar, mirroring where RingilDataTable puts it under the table. */}
      {filterSwitchVisible && <FilterUiVersionSwitch version={filterUiVersion} onToggle={toggleFilterUiVersion} />}

      {visible && (
        <SettingsSidebar>
          <CalendarSettings
            visibleRampsCount={visibleRampsCount}
            onVisibleRampsCountChange={count => {
              restrictCalendarCols(count);
              setVisibleRampsCount(count);
              handleViewToggle('day');
            }}
            currentView={currentView}
            onViewChange={view => {
              if (view === 'day') {
                handleViewToggle('day', visibleRampsCount);
                if (lastCurrentDate) {
                  setCurrentDate(lastCurrentDate);
                }
                return;
              }
              if (!lastCurrentDate) {
                setLastCurrentDate(currentDate);
              }
              handleViewToggle(view);
            }}
            weekDisabled={weekDisabled}
            showLargeView={showLargeView}
            setShowLargeView={setShowLargeView}
            statisticsEnabled={statisticsEnabled}
            statsOpened={statsOpened}
            setStatsOpened={setStatsOpened}
            statsActive={statsActive}
            setStatsActive={setStatsActive}
          />
        </SettingsSidebar>
      )}
    </div>
  );
};